import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/status-badge";
import { SignalProgress } from "@/components/ui/signal-progress";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { 
  Check, X, Flame, Clock, Eye, EyeOff, ChevronRight, ChevronDown,
  MapPin, Loader2, User, Edit, Mail
} from "lucide-react";
import { useEffect, useState } from "react";
import { useCareerSignals } from "@/hooks/useCareerSignals";
import { useWarmup } from "@/hooks/useWarmup";
import { useProfile } from "@/hooks/useProfile";
import { useAuth } from "@/hooks/useAuth";
import { useProfileInterests } from "@/hooks/useInterests";
import { useStudentSkills } from "@/hooks/useStudentSkills";
import { getSkillById } from "@/lib/skills";
import { toast } from "@/hooks/use-toast";

export default function AlunoCarreira() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, isLoading: profileLoading, updateProfile } = useProfile();
  const { signals, userSignals, isLoading: signalsLoading, ensureUserSignals } = useCareerSignals();
  const { prompts, responses, isLoading: warmupLoading } = useWarmup();
  const { interests } = useProfileInterests();
  const { skills } = useStudentSkills();
  const [profileOpen, setProfileOpen] = useState(false);

  useEffect(() => {
    if (signals.length > 0 && userSignals.length === 0 && !signalsLoading) {
      ensureUserSignals.mutate();
    }
  }, [signals.length, userSignals.length, signalsLoading]);

  const isLoading = profileLoading || signalsLoading || warmupLoading;

  if (isLoading) {
    return (
      <div className="page-container flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const doneCount = userSignals.filter(s => s.status === "done").length;
  const totalSignals = userSignals.length || signals.length;
  const profileStatus = (profile?.profile_status ?? "construction") as "construction" | "evolution" | "ready";
  const visibilidade = profile?.visibility_global ?? false;
  const canToggleVisibilidade = profileStatus === "ready";

  const handleToggleVisibility = async () => {
    if (!canToggleVisibilidade) return;
    try {
      await updateProfile.mutateAsync({ visibility_global: !visibilidade });
      toast({ title: visibilidade ? "Perfil ocultado" : "Perfil visível para empresas" });
    } catch {
      toast({ title: "Erro ao alterar visibilidade", variant: "destructive" });
    }
  };

  const sinaisCarreira = signals.map(s => {
    const us = userSignals.find(us => us.signal_id === s.id);
    return { id: s.id, label: s.name, completed: us?.status === "done" };
  });

  const aquecimentos = prompts.slice(0, 3).map(p => {
    const resp = responses.find(r => r.prompt_id === p.id);
    return { id: p.id, titulo: p.title, concluido: resp?.status === "submitted" };
  });

  const hasBio = !!profile?.bio;
  const hasObjetivo = !!profile?.objective;
  const hasInteresses = interests.length > 0;
  const hasSkills = skills.length > 0;

  return (
    <div className="page-container">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-foreground">Carreira</h1>
        <p className="text-muted-foreground text-sm mt-1">Seu perfil, evolução e visibilidade</p>
      </motion.div>

      {/* Perfil + Status combinados */}
      <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="mb-6">
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          {/* Profile header - always visible */}
          <button
            onClick={() => setProfileOpen(!profileOpen)}
            className="w-full flex items-center justify-between p-4 hover:bg-accent/5 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <User className="w-6 h-6 text-primary" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-foreground">{profile?.full_name || "Meu Perfil"}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <StatusBadge status={profileStatus} />
                  {visibilidade ? (
                    <Badge variant="secondary" className="text-[10px] gap-0.5 h-5">
                      <Eye className="w-3 h-3" /> Visível
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] gap-0.5 h-5 text-muted-foreground">
                      <EyeOff className="w-3 h-3" /> Oculto
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <SignalProgress current={doneCount} total={totalSignals} />
              <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${profileOpen ? "rotate-180" : ""}`} />
            </div>
          </button>

          {/* Expanded profile details */}
          {profileOpen && (
            <div className="border-t border-border p-4 space-y-4">
              {/* Contact info */}
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-muted-foreground text-sm">
                  <Mail className="w-4 h-4" />
                  <span>{user?.email ?? "—"}</span>
                </div>
                {(profile?.city || profile?.state) && (
                  <div className="flex items-center gap-1.5 text-muted-foreground text-sm">
                    <MapPin className="w-4 h-4" />
                    <span>{[profile.city, profile.state].filter(Boolean).join(", ")}</span>
                  </div>
                )}
              </div>

              {/* Bio */}
              {hasBio && (
                <div>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-1">Bio</h3>
                  <p className="text-sm text-foreground">{profile?.bio}</p>
                </div>
              )}

              {/* Objetivo */}
              {hasObjetivo && (
                <div>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-1">Objetivo</h3>
                  <Badge variant="default" className="text-xs">{profile?.objective}</Badge>
                </div>
              )}

              {/* Interesses */}
              {hasInteresses && (
                <div>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-1">Interesses</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {interests.map((item) => (
                      <Badge key={item.tag_id} variant="secondary" className="text-xs">
                        {(item as any).interest_tags?.name ?? item.tag_id}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Habilidades */}
              {hasSkills && (
                <div>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-1">Habilidades</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {skills.map((skillId) => {
                      const skill = getSkillById(skillId);
                      return (
                        <Badge key={skillId} variant="default" className="text-xs">
                          {skill?.label ?? skillId}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}

              <Button variant="outline" size="sm" className="w-full" onClick={() => navigate("/aluno/perfil/editar")}>
                <Edit className="w-4 h-4 mr-1.5" />Editar Perfil
              </Button>
            </div>
          )}
        </div>
      </motion.section>

      {/* Sinais de Carreira */}
      <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mb-6">
        <h2 className="font-heading text-base font-semibold text-foreground mb-3">Sinais de Carreira</h2>
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          {sinaisCarreira.map((sinal, i) => (
            <div key={sinal.id} className={`flex items-center gap-3 p-4 ${i < sinaisCarreira.length - 1 ? "border-b border-border" : ""}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${sinal.completed ? "bg-success text-success-foreground" : "bg-muted"}`}>
                {sinal.completed ? <Check className="w-4 h-4" /> : <X className="w-4 h-4 text-muted-foreground" />}
              </div>
              <span className={`text-sm ${sinal.completed ? "text-foreground" : "text-muted-foreground"}`}>{sinal.label}</span>
            </div>
          ))}
        </div>
        <div className="bg-accent/10 rounded-xl p-3 mt-2 border border-accent/20">
          <p className="text-xs text-muted-foreground">
            {profileStatus === "ready"
              ? "✅ Perfil apto — visível para empresas parceiras"
              : "Complete ao menos 1 experiência e faça o aquecimento para evoluir"}
          </p>
        </div>
      </motion.section>

      {/* Aquecimento */}
      <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-base font-semibold text-foreground flex items-center gap-2">
            <Flame className="w-5 h-5 text-destructive" />Aquecimento
          </h2>
          <Button variant="link" size="sm" onClick={() => navigate("/aluno/carreira/historico")}>
            Ver histórico<ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <div className="bg-accent/10 rounded-xl p-4 mb-3 border border-accent/20">
          <p className="text-sm text-foreground font-medium">Isso não é teste. É aquecimento.</p>
          <p className="text-xs text-muted-foreground mt-1">Atividades rápidas (10-15 min) para refletir sobre sua carreira.</p>
        </div>
        <div className="space-y-2">
          {aquecimentos.map((aq) => (
            <div key={aq.id} className="flex items-center gap-3 p-4 bg-card rounded-xl border border-border">
              {aq.concluido ? <Check className="w-5 h-5 text-success" /> : <Clock className="w-5 h-5 text-muted-foreground" />}
              <span className="flex-1 text-sm text-foreground">{aq.titulo}</span>
              {!aq.concluido && (
                <Button size="sm" variant="accent" onClick={() => navigate(`/aluno/carreira/aquecimento/${aq.id}`)}>Fazer</Button>
              )}
            </div>
          ))}
          {aquecimentos.length === 0 && (
            <p className="text-sm text-muted-foreground text-center p-4">Nenhum aquecimento disponível ainda.</p>
          )}
        </div>
      </motion.section>

      {/* Visibilidade para Parceiros */}
      <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="mb-6">
        <h2 className="font-heading text-base font-semibold text-foreground mb-3">Visibilidade para Parceiros</h2>
        <div className="bg-card rounded-2xl p-4 border border-border">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              {visibilidade ? <Eye className="w-5 h-5 text-success" /> : <EyeOff className="w-5 h-5 text-muted-foreground" />}
              <div>
                <p className="text-sm font-medium text-foreground">{visibilidade ? "Visível" : "Oculto"}</p>
                <p className="text-xs text-muted-foreground">Seu perfil {visibilidade ? "aparece" : "não aparece"} para empresas</p>
              </div>
            </div>
            <button onClick={handleToggleVisibility}
              className={`w-12 h-7 rounded-full transition-colors ${visibilidade ? "bg-success" : "bg-muted"} ${!canToggleVisibilidade ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
              disabled={!canToggleVisibilidade}>
              <div className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform ${visibilidade ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
          {!canToggleVisibilidade && (
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground">Para ativar, você precisa ter status "Apto". Complete seus sinais de carreira.</p>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-3 text-center">Você controla quando e como seu perfil aparece.</p>
        </div>
      </motion.section>
    </div>
  );
}
